import { ActionTree } from 'vuex'
import config from 'config'
import i18n from '@vue-storefront/i18n'
import { TaskQueue } from '@vue-storefront/core/lib/sync'
import { htmlDecode } from '@vue-storefront/core/filters'
import * as coreTypes from '@vue-storefront/core/modules/wishlist/store/mutation-types'
import * as types from './mutation-types'
import { processURLAddress } from '@vue-storefront/core/helpers'
import { adjustMultistoreApiUrl } from '@vue-storefront/core/lib/multistore'
import RootState from '@vue-storefront/core/types/RootState'
import MagentoWishlistState from '../types/MagentoWishlistState'
import { Logger } from '@vue-storefront/core/lib/logger'
import rootStore from '@vue-storefront/core/store'
import { prepareQuery } from '@vue-storefront/core/modules/catalog/queries/common'
import { StorageManager } from '@vue-storefront/core/lib/storage-manager'
// import { cacheStorage } from '@vue-storefront/core/modules/wishlist'

const actions: ActionTree<MagentoWishlistState, RootState> = {
  clear ({ state, commit, getters }): Promise<Response> {
    return new Promise((resolve, reject) => {
      const clear = () => {
        commit(coreTypes.WISH_DEL_ALL_ITEMS, [])
      }

      if (rootStore.state.user.current) {
        let url = processURLAddress(config.magentoWishlist.endpoint) + '?token={{token}}'
        url = config.storeViews.multistore ? adjustMultistoreApiUrl(url) : url
        TaskQueue.execute({ url,
          payload: {
            method: 'DELETE',
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            },
            mode: 'cors',
            body: JSON.stringify({
              items: state.items.map(item => getters.getItemId(item))
            })
          }
        }).then(resp => {
          if (resp.resultCode === 200 && resp.result.every(v => v)) {
            clear()
            resolve(resp.result)
          } else {
            reject(resp)
          }
        }).catch(err => {
          reject(err)
        })
      } else {
        clear()
        resolve()
      }
    })
  },
  async load ({ state, commit, getters }, force: boolean = false): Promise<Response> {
    if (getters.loadStatus || (!force && getters.isWishlistLoaded)) return

    const wishListStorage = StorageManager.get('wishlist')

    commit(types.SET_WISHLIST_START_LOAD)
    return new Promise((resolve, reject) => {
      if (rootStore.state.user && rootStore.state.user.current) {
        let url = processURLAddress(config.magentoWishlist.endpoint) + '?token={{token}}'
        url = config.storeViews.multistore ? adjustMultistoreApiUrl(url) : url

        TaskQueue.execute({ url,
          payload: {
            method: 'GET',
            headers: {
              'Accept': 'application/json'
            },
            mode: 'cors'
          }
        }).then(resp => {
          if (resp.resultCode === 200) {
            commit(coreTypes.SET_WISHLIST_LOADED)
            commit(coreTypes.WISH_LOAD_WISH, resp.result.items.map(item => item.product))
            commit(types.SET_WISHLIST_ITEM_IDS, resp.result.items)
            Logger.info('Wishlist state loaded from magento. ', 'api', state.items)()
            wishListStorage.setItem('current-wishlist', state.items)

            if (resp.result.items_count > 0) {
              const productSKUs = state.items.map(item => item.sku)
              const skuQuery = prepareQuery({ filters: [
                { key: 'sku', value: { 'in': productSKUs } }
              ] })

              rootStore.dispatch('product/list', {
                query: skuQuery
              }).then(res => {
                if (res) {
                  commit(coreTypes.WISH_LOAD_WISH, res.items)
                  wishListStorage.setItem('current-wishlist', state.items)
                }
              })
            }

            resolve(resp.result)
          } else {
            reject(resp)
          }
        }).catch(err => {
          reject(err)
        }).finally(() => {
          commit(types.SET_WISHLIST_END_LOAD)
        })
      } else {
        commit(coreTypes.SET_WISHLIST_LOADED)
        rootStore.dispatch('wishlist/loadFromCache').then(storedItems => {
          commit(coreTypes.WISH_LOAD_WISH, storedItems)
          resolve(storedItems)
        }).catch(err => {
          reject(err)
        }).finally(() => {
          commit(types.SET_WISHLIST_END_LOAD)
        })
      }
    })
  },
  loadFromCache () {
    const wishlistStorage = StorageManager.get('wishlist')
    return wishlistStorage.getItem('current-wishlist')
  },
  addItem ({ state, commit, dispatch }, product): Promise<Response> {
    const wishListStorage = StorageManager.get('wishlist')
    return new Promise((resolve, reject) => {
      const addItem = product => {
        commit(coreTypes.WISH_ADD_ITEM, { product })
        rootStore.dispatch('notification/spawnNotification', {
          type: 'success',
          message: i18n.t('Product {productName} has been added to wishlist!', { productName: htmlDecode(product.name) }),
          action1: { label: i18n.t('OK') }
        })
        wishListStorage.setItem('current-wishlist', state.items).catch((reason) => {
          Logger.error(reason, 'wishlist') // it doesn't work on SSR
        })
        if (rootStore.state.user.current) {
          dispatch('load', true)
        }
        resolve(product)
      }

      if (rootStore.state.user.current) {
        let url = processURLAddress(config.magentoWishlist.endpoint) + '/' + encodeURIComponent(product.parentSku) + '?token={{token}}'
        url = config.storeViews.multistore ? adjustMultistoreApiUrl(url) : url

        TaskQueue.execute({ url,
          payload: {
            method: 'PUT',
            headers: {
              'Accept': 'application/json'
            },
            mode: 'cors'
          }
        }).then(resp => {
          if (resp.resultCode === 200 && resp.result === true) {
            addItem(product)
          } else {
            reject(resp)
          }
        }).catch(err => {
          reject(err)
        })
      } else {
        addItem(product)
      }
    })
  },
  removeItem ({ state, getters, commit }, product): Promise<Response> {
    const wishListStorage = StorageManager.get('wishlist')
    return new Promise((resolve, reject) => {
      const removeItem = product => {
        commit(coreTypes.WISH_DEL_ITEM, { product })
        rootStore.dispatch('notification/spawnNotification', {
          type: 'success',
          message: i18n.t('Product {productName} has been removed from wishlist!', { productName: htmlDecode(product.name) }),
          action1: { label: i18n.t('OK') }
        })
        wishListStorage.setItem('current-wishlist', state.items).catch((reason) => {
          Logger.error(reason, 'wishlist') // it doesn't work on SSR
        })
        resolve(product)
      }

      if (rootStore.state.user.current) {
        let wishItemId = getters.getItemId(product)

        if (typeof wishItemId === 'undefined') {
          removeItem(product)
          return
        }

        let url = processURLAddress(config.magentoWishlist.endpoint) + '/' + wishItemId + '?token={{token}}'
        url = config.storeViews.multistore ? adjustMultistoreApiUrl(url) : url

        TaskQueue.execute({ url,
          payload: {
            method: 'DELETE',
            headers: {
              'Accept': 'application/json'
            },
            mode: 'cors'
          }
        }).then(resp => {
          if (resp.resultCode === 200 && resp.result === true) {
            commit(types.WISHLIST_DEL_ITEM_ID, { product })
            removeItem(product)
          } else {
            reject(resp)
          }
        }).catch(err => {
          reject(err)
        })
      } else {
        removeItem(product)
      }
    })
  }
}

export default actions
